import React, { type ComponentProps, useCallback, useRef } from 'react';
import { ImpactFeedbackStyle } from 'expo-haptics';
import {
  Pressable,
  type PressableStateCallbackType,
  type StyleProp,
  StyleSheet,
  type TouchableOpacity,
  type ViewStyle,
} from 'react-native';

import {
  BodyText,
  LoadingDots,
  theme,
  triggerHapticFeedback,
  useHovered,
  webOnlyStyles,
} from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuDietaryPreferencesSubmitButton = (
  props: MenuDietaryPreferencesSubmitButtonProps,
) => {
  const { style, isLoading, isDisabled, onPress, label, size = 'md', ...restProps } = props;

  const ref = useRef(null);
  const isHovered = useHovered(ref);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleOnPress = useCallback(() => {
    if (isLoading || isDisabled || !onPress) return;

    void triggerHapticFeedback(ImpactFeedbackStyle.Medium);
    onPress();
  }, [isLoading, isDisabled, onPress]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const pressableStyles = useCallback(
    (state: PressableStateCallbackType): StyleProp<ViewStyle> => {
      const { pressed } = state;

      return [
        styles.pressable,
        size === 'lg' ? styles.pressableLG : styles.pressableMD,
        pressableWebStyles,
        !isDisabled && isHovered ? styles.pressableHover : undefined,
        !isDisabled && pressed ? styles.pressablePressed : undefined,
        isDisabled ? styles.pressableDisabled : undefined,
        style,
      ];
    },
    [isDisabled, size, isHovered, style],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Pressable
      ref={ref}
      accessibilityRole="button"
      aria-label={label}
      aria-busy={isLoading}
      style={pressableStyles}
      onPress={handleOnPress}
      disabled={isDisabled || isLoading}
      {...restProps}
    >
      {isLoading ? (
        <LoadingDots />
      ) : (
        <BodyText
          style={isDisabled ? styles.labelDisabled : styles.label}
          sizeMatch={PRESSABLE_SIZE_MAP[size]}
          numberOfLines={1}
        >
          {label}
        </BodyText>
      )}
    </Pressable>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PRESSABLE_SIZE_MAP: Record<Size, ComponentProps<typeof BodyText>['sizeMatch']> = {
  md: ['18'],
  lg: ['20'],
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pressable: {
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: theme.colors.KALE,
    borderRadius: 64,
    userSelect: 'none',
  },
  pressableMD: {
    paddingVertical: theme.spacing['2'],
    paddingHorizontal: theme.spacing['8'],
    minHeight: 64,
    minWidth: 230,
  },
  pressableLG: {
    paddingVertical: theme.spacing['5'],
    paddingHorizontal: theme.spacing['8'],
    minHeight: 72,
    minWidth: 242,
  },
  pressableHover: {
    backgroundColor: theme.colors.OPACITY.SPINACH.NEAR_LIGHTEST,
  },
  pressablePressed: {
    opacity: 0.7,
  },
  pressableDisabled: {
    borderColor: theme.colors.LIGHT_CHARCOAL,
    opacity: 0.75,
  },
  label: {
    textAlign: 'center',
    color: theme.colors.KALE,
  },
  labelDisabled: {
    textAlign: 'center',
    color: theme.colors.LIGHT_CHARCOAL,
  },
});

const pressableWebStyles = webOnlyStyles({
  outlineColor: theme.colors.GRAY,
  outlineOffset: theme.spacing['1'],
  outlineWidth: 2,
  transition: `background-color ${theme.transitions.base}ms, border-color ${theme.transitions.base}ms, opacity ${theme.transitions.base}ms`,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuDietaryPreferencesSubmitButtonProps = {
  label: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  onPress: () => void;
  size?: Size;
  style?: StyleProp<ViewStyle>;
} & Omit<
  ComponentProps<typeof TouchableOpacity>,
  'children' | 'ref' | 'onPress' | 'disabled' | 'style'
>;

type Size = 'md' | 'lg';
